import React, { FC, ReactNode, MouseEvent, KeyboardEvent } from 'react';
import { Link, LinkProps } from 'react-router-dom';

import classNames from 'classnames';
import NotificationMessage from 'src/common/components/NotificationMessage';
import getClassName from 'src/common/utils/getClassName';
import { BackLink } from '../../../common/components/BackButton';
import Button, { ButtonProps } from '../../../common/components/Button';
import s from './CTA.module.scss';

type CTALinkProps = {
  children: ReactNode;
  classNameModifier?: string;
  disabled?: boolean;
  'data-test'?: string;
} & LinkProps;

export const CTALink: FC<CTALinkProps> = (
  ({ children, classNameModifier, disabled, ...restProps }: CTALinkProps) => {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
      if (disabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    return (
      <Link
        className={getClassName(s, 'button', classNames(classNameModifier, { disabled }))}
        aria-disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        {...restProps}
      >
        {children}
      </Link>
    );
  }
);

type CTABackLinkProps = Parameters<typeof BackLink>[0];
export const CTABackLink: FC<CTABackLinkProps> = (props) => (
  <BackLink
    classNameModifier="inbox"
    {...props}
  />
);
type CTAExternalLinkProps = {
  children: ReactNode;
  classNameModifier?: string;
};
export const CTAExternalLink: FC<CTAExternalLinkProps> = (
  ({ children, classNameModifier, ...restProps }: CTAExternalLinkProps) => (
    <a
      className={getClassName(s, 'button', classNameModifier)}
      {...restProps}
    >
      {children}
    </a>
  )
);

export const CTAButton: FC<ButtonProps> = ({ classNameModifier = '', ...restProps }) => (
  <Button
    classNameModifier={`inbox CTA ${classNameModifier}`}
    fullWidth
    {...restProps}
  />
);

type CTAErrorProps = {
  children: ReactNode;
};
export const CTAError: FC<CTAErrorProps> = (
  ({ children }: CTAErrorProps) => (
    <div className={s.notificationWrapper}>
      <NotificationMessage
        classNameModifier="centered ctaValidationError"
        outdent={false}
      >
        {children}
      </NotificationMessage>
    </div>
  )
);

type CTAWrapperProps = {
  children: ReactNode;
};
export const CTAWrapper: FC<CTAWrapperProps> = (
  ({ children }: CTAWrapperProps) => (
    <div className={s.ctaContentWrapper}>
      {children}
    </div>
  )
);
